import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Camera, Zap, BrainCircuit, TrendingUp } from 'lucide-react';
const steps = [{
  icon: Camera,
  title: 'Capture',
  desc: 'Authentic content from ERA',
  angle: -90
}, {
  icon: Zap,
  title: 'Activate',
  desc: 'Turn into interactive Snaks',
  angle: 0
}, {
  icon: BrainCircuit,
  title: 'Learn',
  desc: 'Gather behavioral insights',
  angle: 90
}, {
  icon: TrendingUp,
  title: 'Scale',
  desc: 'Improve future activations',
  angle: 180
}];
const WHEEL_SIZE = 480;
const RADIUS = 200;
const CENTER = WHEEL_SIZE / 2;
export const Slide06Flywheel = () => {
  return <div className="relative w-full h-full flex flex-col bg-slate-950 overflow-hidden" data-id="element-134">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(30,30,40,0.6),_rgba(2,6,23,1)_70%)]" data-id="element-135" />

      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-24 py-12" data-id="element-136">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="text-center mb-8" data-id="element-137">
          <div className="flex items-center justify-center space-x-3 mb-4" data-id="element-138">
            <div className="h-[1px] w-8 bg-red-600" data-id="element-139" />
            <span className="text-red-500 font-semibold tracking-widest uppercase text-xs" data-id="element-140">
              06 — The Flywheel
            </span>
            <div className="h-[1px] w-8 bg-red-600" data-id="element-141" />
          </div>
          <h2 className="text-4xl font-bold tracking-tight mb-3 text-white" data-id="element-142">
            Capture <span className="text-red-500" data-id="element-143">→</span> Activate{' '}
            <span className="text-red-500" data-id="element-144">→</span> Learn{' '}
            <span className="text-red-500" data-id="element-145">→</span> Scale
          </h2>
          <p className="text-base text-slate-400 font-light" data-id="element-146">
            ERA becomes the first building block in a continuous cycle.
          </p>
        </motion.div>

        {/* Flywheel — perfect square container */}
        <div className="relative" style={{
        width: WHEEL_SIZE,
        height: WHEEL_SIZE
      }} data-id="element-147">
          {/* SVG ring + connectors */}
          <svg className="absolute inset-0" width={WHEEL_SIZE} height={WHEEL_SIZE} viewBox={`0 0 ${WHEEL_SIZE} ${WHEEL_SIZE}`} data-id="element-148">
            <defs data-id="element-149">
              <linearGradient id="ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-150">
                <stop offset="0%" stopColor="#ef4444" stopOpacity="0.2" data-id="element-151" />
                <stop offset="50%" stopColor="#ef4444" stopOpacity="0.8" data-id="element-152" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.2" data-id="element-153" />
              </linearGradient>
            </defs>

            {/* Dotted background ring */}
            <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke="rgba(148,163,184,0.12)" strokeWidth="1" strokeDasharray="2 6" data-id="element-154" />

            {/* Animated red gradient ring */}
            <motion.circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" stroke="url(#ring-gradient)" strokeWidth="2" strokeLinecap="round" pathLength="1" initial={{
            pathLength: 0,
            opacity: 0
          }} animate={{
            pathLength: 1,
            opacity: 1
          }} transition={{
            duration: 2,
            ease: 'easeInOut',
            delay: 0.4
          }} transform={`rotate(-90 ${CENTER} ${CENTER})`} data-id="element-155" />

            {/* Direction arrows at midpoints */}
            {steps.map((step, i) => {
            const next = steps[(i + 1) % steps.length];
            const midAngle = (step.angle + (i === steps.length - 1 ? 360 : next.angle)) / 2;
            const rad = midAngle * Math.PI / 180;
            const ax = CENTER + Math.cos(rad) * RADIUS;
            const ay = CENTER + Math.sin(rad) * RADIUS;
            const rotate = midAngle + 90;
            return <motion.g key={`arrow-${i}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1.6 + i * 0.1,
              duration: 0.4
            }} transform={`translate(${ax} ${ay}) rotate(${rotate})`} data-id="element-156">
                  <path d="M -5 -4 L 5 0 L -5 4 Z" fill="#ef4444" data-id="element-157" />
                </motion.g>;
          })}
          </svg>

          {/* Center ERA node */}
          <motion.div initial={{
          scale: 0.6,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} className="absolute" style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        }} data-id="element-158">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-red-600 to-red-900 border border-red-400/40 flex flex-col items-center justify-center shadow-[0_0_60px_rgba(220,38,38,0.45)]" data-id="element-159">
              <span className="text-[10px] tracking-[0.25em] uppercase text-red-200 mb-1" data-id="element-160">
                Ignition
              </span>
              <span className="text-2xl font-bold text-white tracking-wider" data-id="element-161">
                ERA
              </span>
            </div>
          </motion.div>

          {/* Step nodes — wrapper handles position, inner motion handles scale */}
          {steps.map((step, index) => {
          const rad = step.angle * Math.PI / 180;
          const x = Math.cos(rad) * RADIUS;
          const y = Math.sin(rad) * RADIUS;
          const Icon = step.icon;
          return <div key={step.title} className="absolute" style={{
            top: '50%',
            left: '50%',
            transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`
          }} data-id="element-162">
                <motion.div initial={{
              opacity: 0,
              scale: 0.4
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.7 + index * 0.18,
              duration: 0.5,
              type: 'spring',
              stiffness: 140
            }} className="flex flex-col items-center w-44 text-center" data-id="element-163">
                  <div className="w-16 h-16 rounded-2xl bg-slate-900 border border-red-500/40 flex items-center justify-center mb-3 shadow-lg shadow-red-950/40 relative" data-id="element-164">
                    <div className="absolute inset-0 rounded-2xl bg-red-500/10" data-id="element-165" />
                    <Icon className="w-7 h-7 text-red-400 relative z-10" strokeWidth={1.5} data-id="element-166" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-1" data-id="element-167">
                    {step.title}
                  </h4>
                  <p className="text-xs text-slate-400 font-light leading-snug px-2" data-id="element-168">
                    {step.desc}
                  </p>
                </motion.div>
              </div>;
        })}
        </div>

        {/* Framework outcomes */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1.8,
        duration: 0.8
      }} className="mt-8 flex items-center space-x-5" data-id="element-169">
          <span className="text-xs uppercase tracking-[0.3em] text-slate-500" data-id="element-170">
            Outcomes
          </span>
          <div className="flex items-center space-x-4" data-id="element-171">
            {['Attract', 'Retain', 'Mobilize'].map((word, i) => <Fragment key={word} data-id="element-172">
                <span className="text-sm font-semibold text-white tracking-wide" data-id="element-173">
                  {word}.
                </span>
                {i < 2 && <span className="w-1 h-1 rounded-full bg-red-500" data-id="element-174" />}
              </Fragment>)}
          </div>
        </motion.div>
      </div>
    </div>;
};